import { isPresent } from 'utils/isPresent';
import { createBrowserHistory } from 'history';
import { change } from 'redux-form';
import AnalyticsApi from '../../Services/Analytics/AnalyticsApi.js';
import {
  groupWithEmployeeCount,
  groupWithIndustries,
  groupWithBusinessName,
  groupWithBusinessPhone,
} from '../../Services/Analytics/GroupPropertiesService.js';
import SetupApi from '../api/SetupApi.js';
import OrganizationApi from '../../../Resources/Organization/OrganizationApi';
import ProApi from '../../../Resources/Pro/ProApi.js';
import { receivedServerData } from '../../Common/Data/DataFetcher.js';
import { unmaskPhoneNumber } from '../../Onboarding/Unmasker.js';
import {
  ORGANIZATION_NAMESPACE,
  ORGANIZATION_INTERESTS_NAMESPACE,
} from '../../../Resources/Organization/OrganizationData.js';
import { PRO_NAMESPACE } from '../../../Resources/Pro/ProData.js';
import {
  trackOnboardingCompleted,
  trackButtonClicked,
  trackBusinessInfoSubmitted,
} from '../../Services/Analytics/TrackEventsService.js';
import Utils from '../../Common/Utils.js';
import ProgressiveApi from '../ProgressiveOnboarding/ProgressiveApi';
import RestAPI from 'components/Common/restApi';

export const SESSION_HISTORY = 'SESSION_HISTORY';
export const FORM_SUBMITTING = 'FORM_SUBMITTING';
export const ERROR_TEXT = 'ERROR_TEXT';
export const SET_SEARCH_TERM = 'SET_SEARCH_TERM';
export let browserHistory;

const ONBOARDING_STAGE = 'setup';

export function initializeBrowserHistory(mobile) {
  var path;
  if (mobile) {
    path = '/mobile-web/setup';
  } else {
    path = '/pro/setup';
  }

  browserHistory = createBrowserHistory({
    basename: path,
  });
}

export function goBack() {
  return (dispatch) => {
    dispatch({ type: SESSION_HISTORY, pop: true });

    browserHistory.goBack();
  };
}

export function updateCurrentPage(newPage) {
  return (dispatch) => {
    if (newPage.includes('/setup/')) {
      dispatch({ type: SESSION_HISTORY, newPage: newPage });
      let pageName = newPage.split('/').pop();
      browserHistory.push(`/${pageName}`);
    } else {
      window.location = newPage;
    }
  };
}

export function goToManualAddress() {
  return (dispatch) => {
    dispatch({ type: SESSION_HISTORY, newPage: 'manual_address' });
    browserHistory.push('/manual_address');
  };
}

export function disableForm() {
  return (dispatch) => {
    dispatch({ type: FORM_SUBMITTING, formSubmitting: true });
  };
}

export function enableForm() {
  return (dispatch) => {
    dispatch({ type: FORM_SUBMITTING, formSubmitting: false });
  };
}

export function updateOrganization(formData, event) {
  return (dispatch) => {
    const onboarding_event = { event: event, stage: ONBOARDING_STAGE };

    if (isPresent(formData)) {
      const form_data = Object.assign(
        {},
        formData.street && { street: formData.street },
        formData.street_line_2 && { street_line_2: formData.street_line_2 },
        formData.city && { city: formData.city },
        formData.state && { state: formData.state },
        formData.zip && { postal_code: formData.zip },
        formData.country && { country: formData.country },
        formData.latitude && { latitude: formData.latitude },
        formData.longitude && { longitude: formData.longitude }
      );

      return Promise.all([
        OrganizationApi.updateOrganization(form_data),
        SetupApi.advanceOnboardingState(onboarding_event),
      ])
        .then((response) => {
          dispatch(receivedServerData(response[0].data, ORGANIZATION_NAMESPACE));
          dispatch(maybeGoToDashboard(response[1].data.page_url));
        })
        .catch((error) => {
          const errorMessage = Utils.parseErrorMessage(error);
          dispatch({ type: ERROR_TEXT, errorText: errorMessage });
        });
    }
  };
}

export function updateBusinessInfo(formData, event, useSalesAssisted) {
  return (dispatch) => {
    const onboarding_event = { event: event, stage: ONBOARDING_STAGE };

    if (isPresent(formData)) {
      const orgData = {
        business_phone: unmaskPhoneNumber(formData.businessPhone),
        business_name: formData.businessName,
        business_website: formData.businessWebsite,
      };
      dispatch(lookUpPhoneCarrierType(unmaskPhoneNumber(formData.businessPhone)))
        .then((response) => {
          dispatch(trackBusinessInfoSubmitted(true, useSalesAssisted));
        })
        .catch((error) => {
          dispatch(trackBusinessInfoSubmitted(false, useSalesAssisted));
        });

      const proData = {
        mobile_number: isPresent(formData.mobileNumber) ? formData.mobileNumber : formData.businessPhone,
      };

      return Promise.all([
        OrganizationApi.updateOrganization(orgData),
        SetupApi.advanceOnboardingState(onboarding_event),
        ProApi.updatePro(proData),
      ])
        .then((response) => {
          dispatch(groupWithBusinessName(orgData.business_name));
          dispatch(groupWithBusinessPhone(orgData.business_phone));
          dispatch(receivedServerData(response[0].data, ORGANIZATION_NAMESPACE));
          dispatch(receivedServerData(response[2].data, PRO_NAMESPACE));
          dispatch(maybeGoToDashboard(response[1].data.page_url));
        })
        .catch((error) => {
          dispatch({ type: ERROR_TEXT, errorText: error.message });
        })
        .finally(() => {
          SetupApi.hasNoWebsite(formData.hasNoWebsite);
          AnalyticsApi.checkAndReportDomain();
        });
    }
  };
}

export function setProNameAndPassword(formData, event) {
  return (dispatch, getState) => {
    const onboarding_event = { event: event, stage: ONBOARDING_STAGE };
    if (isPresent(formData)) {
      const password_data = { password: formData.password };
      const pro_data = { first_name: formData.firstName, last_name: formData.lastName };

      return ProApi.updatePro(pro_data)
        .then(() => {
          const passwordFunction = isPresent(formData.password)
            ? SetupApi.setPassword(password_data)
            : new Promise((resolve) => resolve());

          return passwordFunction.then(() => {
            return SetupApi.advanceOnboardingState(onboarding_event).then((response) => {
              const nextPage = response.data.page_url;
              dispatch(maybeGoToDashboard(nextPage));
            });
          });
        })
        .catch((error) => {
          dispatch({ type: ERROR_TEXT, errorText: Utils.parseErrorMessage(error) });
        });
    }
  };
}

export function finishBookDemo(buttonName, useSalesAssisted) {
  return (dispatch) => {
    const event = { event: 'done_book_demo', stage: ONBOARDING_STAGE };

    dispatch(trackButtonClicked(buttonName, {}, useSalesAssisted));

    return Promise.resolve()
      .then(() => {
        return SetupApi.advanceOnboardingState(event);
      })
      .then((response) => {
        const nextPage = response.data.page_url;
        dispatch(maybeGoToDashboard(nextPage, useSalesAssisted));
      })
      .catch((error) => {
        dispatch({ type: ERROR_TEXT, errorText: error.message });
      });
  };
}

export function finishBookDemoWithCalendly(useSalesAssisted) {
  return (dispatch) => {
    const event = { event: 'done_book_demo', stage: ONBOARDING_STAGE };

    dispatch(trackButtonClicked('book a demo', {}, useSalesAssisted));

    return SetupApi.advanceOnboardingState(event)
      .then((response) => {
        const nextPage = response.data.page_url;
        window.addEventListener('message', function (e) {
          if (e.data.event && e.data.event.indexOf('calendly.event_scheduled') === 0) {
            ProgressiveApi.updateDemoBooked();
            dispatch(maybeGoToDashboard(nextPage, useSalesAssisted));
          }
        });
      })
      .catch((error) => {
        dispatch({ type: ERROR_TEXT, errorText: error.message });
      });
  };
}

function maybeGoToDashboard(nextPage, useSalesAssisted) {
  return (dispatch, getState) => {
    if (!nextPage.includes('setup')) {
      dispatch(trackOnboardingCompleted(...[,], useSalesAssisted));
    }

    if (!nextPage.includes('setup') && getState().mobile) {
      var completed = document.getElementById(COMPLETE_ONBOARDING_ELEMENT);
      if (isPresent(completed)) {
        completed.click();
      }
    } else {
      dispatch(updateCurrentPage(nextPage));
      dispatch(enableForm());
    }
  };
}

export function updateOrganizationInfo(formData, event) {
  return (dispatch) => {
    const onboarding_event = { event: event, stage: ONBOARDING_STAGE };
    const interests = { interests: formData.interests };

    return Promise.all([
      OrganizationApi.updateOrganizationInterests(interests),
      SetupApi.advanceOnboardingState(onboarding_event),
    ])
      .then((response) => {
        dispatch(receivedServerData(response[0].data, ORGANIZATION_INTERESTS_NAMESPACE));
        dispatch(updateCurrentPage(response[1].data.page_url));
      })
      .catch((error) => {
        dispatch({ type: ERROR_TEXT, errorText: error.message });
      });
  };
}

export function updateIndustry(industryId) {
  return (dispatch) => {
    return OrganizationApi.updateOrganization({ industry_id: industryId })
      .then((response) => {
        dispatch(groupWithIndustries([response.data.default_industry.name]));
        dispatch(receivedServerData(response.data, ORGANIZATION_NAMESPACE));
      })
      .catch((error) => {
        dispatch({ type: ERROR_TEXT, errorText: error.message });
      });
  };
}

export function updateEmployees(employeeCount, salesAssisted) {
  return (dispatch) => {
    dispatch(trackButtonClicked('org size selected', {}, salesAssisted));
    OrganizationApi.updateOrganization({ employee_count: employeeCount })
      .then((response) => {
        dispatch(groupWithEmployeeCount(employeeCount));
        dispatch(receivedServerData(response.data, ORGANIZATION_NAMESPACE));
      })
      .catch((error) => {
        dispatch({ type: ERROR_TEXT, errorText: error.message });
      });
  };
}

export function setSearchTerm(searchTerm) {
  return (dispatch) => {
    dispatch({ type: SET_SEARCH_TERM, term: searchTerm });
  };
}

export function lookUpPhoneCarrierType(phoneNumber) {
  return (dispatch) => {
    return SetupApi.lookUpPhoneCarrierType({ phone_number: phoneNumber });
  };
}

export function createProExperiment(name, variation) {
  return (dispatch) => {
    const experiments = {
      [name]: variation,
    };

    return ProApi.createProExperiment({ experiments: experiments });
  };
}

export function generateNewPassword(formName, stateName) {
  return (dispatch) => {
    return RestAPI.post('/alpha/pro/secure_passwords')
      .then((response) => {
        dispatch(change(formName, stateName, response.data.password));
      })
      .catch((error) => {
        dispatch({ type: ERROR_TEXT, errorText: error.message });
      });
  };
}
